import { useEffect, useMemo, useState } from 'react'
import { rolesService } from 'api/configuration/roles'
import {
  EMPTY_CURRENT_PERMISSIONS,
  FULL_CURRENT_PERMISSIONS,
  ICurrentPermissions,
} from 'interfaces/currentPermissions'
import { setAccessSnapshot } from './accessSnapshot'
import {
  createLmsAccessFromPayload,
  LmsAccess,
  readSessionAccess,
} from './lmsAccess'

export const useAccessControl = () => {
  const session = readSessionAccess()
  const { hasFullAccess, isSuperadmin } = session

  const [loading, setLoading] = useState(!hasFullAccess)
  const [resolvedFullAccess, setResolvedFullAccess] = useState(hasFullAccess)
  const [currentPermissions, setCurrentPermissions] =
    useState<ICurrentPermissions>(
      hasFullAccess ? FULL_CURRENT_PERMISSIONS : EMPTY_CURRENT_PERMISSIONS
    )

  useEffect(() => {
    if (hasFullAccess) {
      setCurrentPermissions(FULL_CURRENT_PERMISSIONS)
      setResolvedFullAccess(true)
      setLoading(false)
      return
    }

    setLoading(true)
    rolesService
      .getPermissions()
      .then((response) => {
        const data = response.data as ICurrentPermissions
        setCurrentPermissions({
          learningPath: data?.learningPath || [],
          courses: data?.courses || [],
          permissions: data?.permissions || {},
        })
        setResolvedFullAccess(!!data?.isAdmin)
      })
      .catch(() => {
        setCurrentPermissions(EMPTY_CURRENT_PERMISSIONS)
        setResolvedFullAccess(false)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [hasFullAccess])

  const access: LmsAccess = useMemo(
    () =>
      createLmsAccessFromPayload(
        resolvedFullAccess,
        currentPermissions,
        isSuperadmin
      ),
    [resolvedFullAccess, currentPermissions, isSuperadmin]
  )

  useEffect(() => {
    setAccessSnapshot(access)
  }, [access])

  return {
    loading,
    access,
  }
}
